import React, { useState, useEffect, useRef } from 'react';
import { Coffee, Droplets, Calculator, RotateCcw, Scale, ArrowRightLeft, Edit3, Save, X } from 'lucide-react';
import './index.css';

const CoffeeRatioCalculator = () => {
  const [selectedMethod, setSelectedMethod] = useState('v60');
  const [coffeeAmount, setCoffeeAmount] = useState(25);
  const [waterAmount, setWaterAmount] = useState(400);
  const [calculationMode, setCalculationMode] = useState('coffee'); // 'coffee' or 'water'
  const [editingMethod, setEditingMethod] = useState(null);
  const [tempRatio, setTempRatio] = useState({ coffee: 1, water: 16 });
  
  // Referencia al contenedor principal para medir altura
  const containerRef = useRef(null);

  // Ratios corregidos basados en estándares de la industria
  const [brewingMethods, setBrewingMethods] = useState({
    v60: {
      name: 'V60',
      icon: '🌪️',
      color: 'bg-red-500',
      ratio: { coffee: 1, water: 16 },
      description: 'Filtrado por goteo, sabor limpio y brillante',
      editable: true
    },
    chemex: {
      name: 'Chemex',
      icon: '🧪',
      color: 'bg-amber-500',
      ratio: { coffee: 1, water: 17 }, // Chemex típicamente usa ratios más altos por el filtro grueso
      description: 'Filtrado suave, cuerpo medio con filtros especiales',
      editable: true
    },
    aeropress: {
      name: 'AeroPress',
      icon: '💨',
      color: 'bg-blue-500',
      ratio: { coffee: 1, water: 15 }, // AeroPress estándar, no tan concentrado
      description: 'Inmersión y presión, sabor concentrado y limpio',
      editable: true
    },
    kalita: {
      name: 'Kalita Wave',
      icon: '🌊',
      color: 'bg-teal-500',
      ratio: { coffee: 1, water: 16 }, // Similar al V60
      description: 'Fondo plano, extracción uniforme y balanceada',
      editable: true
    },
    french: {
      name: 'Prensa Francesa',
      icon: '🫖',
      color: 'bg-purple-500',
      ratio: { coffee: 1, water: 15 }, // Ratio corregido para prensa francesa
      description: 'Inmersión total, cuerpo completo y sedoso',
      editable: true
    },
    espresso: {
      name: 'Espresso',
      icon: '☕',
      color: 'bg-yellow-600',
      ratio: { coffee: 1, water: 2.5 }, // Ratio moderno de espresso
      description: 'Alta presión, concentrado e intenso (18g → 45ml)',
      editable: true
    },
    moka: {
      name: 'Moka',
      icon: '⚡',
      color: 'bg-orange-500',
      ratio: { coffee: 1, water: 7 }, // Moka es más concentrada que el filtrado
      description: 'Vapor y presión, sabor fuerte y aromático',
      editable: true
    },
    cold: {
      name: 'Cold Brew',
      icon: '🧊',
      color: 'bg-cyan-500',
      ratio: { coffee: 1, water: 5 }, // Cold brew concentrado, se diluye después
      description: 'Concentrado en frío 12-24hrs, se diluye al servir',
      editable: true
    },
    origami: {
      name: 'Origami',
      icon: '🎋',
      color: 'bg-green-500',
      ratio: { coffee: 1, water: 16 },
      description: 'Flujo controlado, claridad y complejidad',
      editable: true
    },
    turkish: {
      name: 'Café Turco',
      icon: '🫖',
      color: 'bg-rose-500',
      ratio: { coffee: 1, water: 9 }, // Café turco es muy concentrado
      description: 'Muy fino, hervido, tradicionalmente dulce',
      editable: true
    },
    siphon: {
      name: 'Siphon',
      icon: '🔬',
      color: 'bg-indigo-500',
      ratio: { coffee: 1, water: 15 },
      description: 'Vacío y vapor, sabor limpio y teatral',
      editable: true
    },
    custom: {
      name: 'Personalizado',
      icon: '⚙️',
      color: 'bg-gray-500',
      ratio: { coffee: 1, water: 16 },
      description: 'Define tu propia proporción ideal',
      editable: true
    }
  });

  const currentMethod = brewingMethods[selectedMethod];

  // Calcular automáticamente cuando cambia el método o los valores
  useEffect(() => {
    const ratio = currentMethod.ratio;
    if (calculationMode === 'coffee') {
      // Calcular agua basado en café
      const calculatedWater = Math.round((coffeeAmount * ratio.water / ratio.coffee) * 10) / 10;
      setWaterAmount(calculatedWater);
    } else {
      // Calcular café basado en agua
      const calculatedCoffee = Math.round((waterAmount * ratio.coffee / ratio.water) * 10) / 10;
      setCoffeeAmount(calculatedCoffee);
    }
  }, [selectedMethod, coffeeAmount, waterAmount, calculationMode, brewingMethods]);

  const handleCoffeeChange = (value) => {
    setCoffeeAmount(value);
    setCalculationMode('coffee');
  };

  const handleWaterChange = (value) => {
    setWaterAmount(value);
    setCalculationMode('water');
  };

  const resetCalculator = () => {
    setCoffeeAmount(25);
    setWaterAmount(400);
    setCalculationMode('coffee');
  };

  const swapCalculationMode = () => {
    setCalculationMode(calculationMode === 'coffee' ? 'water' : 'coffee');
  };

  const startEditingRatio = (methodKey) => {
    setEditingMethod(methodKey);
    setTempRatio({ ...brewingMethods[methodKey].ratio });
  };

  const cancelEditing = () => {
    setEditingMethod(null);
    setTempRatio({ coffee: 1, water: 16 });
  };

  const saveRatio = () => {
    setBrewingMethods(prev => ({
      ...prev,
      [editingMethod]: {
        ...prev[editingMethod],
        ratio: { ...tempRatio }
      }
    }));
    setEditingMethod(null);
  };

  const handleTempRatioChange = (type, value) => {
    setTempRatio(prev => ({
      ...prev,
      [type]: parseFloat(value) || 1
    }));
  };

  const getRatioText = (ratio) => {
    return `1:${Math.round(ratio.water / ratio.coffee * 10) / 10}`;
  };

  const getStrengthIndicator = () => {
    const ratio = waterAmount / coffeeAmount;
    if (ratio <= 4) return { level: 'Extremo', color: 'text-red-800', width: '15%', bg: 'bg-red-800' };
    if (ratio <= 7) return { level: 'Muy Fuerte', color: 'text-red-600', width: '25%', bg: 'bg-red-600' };
    if (ratio <= 12) return { level: 'Fuerte', color: 'text-orange-600', width: '45%', bg: 'bg-orange-500' };
    if (ratio <= 16) return { level: 'Medio', color: 'text-yellow-600', width: '65%', bg: 'bg-yellow-500' };
    if (ratio <= 20) return { level: 'Suave', color: 'text-green-600', width: '85%', bg: 'bg-green-500' };
    return { level: 'Muy Suave', color: 'text-blue-600', width: '100%', bg: 'bg-blue-500' };
  };

  // Calcular absorción de agua más precisa
  const getWaterAbsorption = () => {
    // Diferentes métodos tienen diferentes tasas de absorción
    const absorptionRates = {
      espresso: 1.5, // Menos absorción por presión
      moka: 1.8,
      french: 2.2, // Más absorción por inmersión
      cold: 2.5, // Mayor absorción en frío
      default: 2.0
    };
    
    const rate = absorptionRates[selectedMethod] || absorptionRates.default;
    return coffeeAmount * rate;
  };

  const getFinalVolume = () => {
    const absorption = getWaterAbsorption();
    return Math.max(0, waterAmount - absorption);
  };

  const strength = getStrengthIndicator();

  // Función para enviar la altura al parent
  const sendHeightToParent = () => {
    if (containerRef.current) {
      const height = containerRef.current.scrollHeight;
      // Enviar mensaje al parent con la altura
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ 
          type: 'resize-iframe', 
          height: height,
          appId: 'coffee-ratio-calculator'
        }, '*');
      }
    }
  };

  // Efecto para enviar la altura al parent después de cada renderizado
  useEffect(() => {
    // Pequeño timeout para asegurar que el DOM se ha actualizado completamente
    const timeoutId = setTimeout(() => {
      sendHeightToParent();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedMethod, coffeeAmount, waterAmount, calculationMode, editingMethod]); // Dependencias que pueden afectar la altura

  // Efecto para manejar cambios de tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      sendHeightToParent();
    };

    window.addEventListener('resize', handleResize);
    
    // Enviar altura inicial
    sendHeightToParent();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Limpieza cuando el componente se desmonta
  React.useEffect(() => {
    return () => {
      // Limpieza si es necesaria
    };
  }, []);

  return (
    <div ref={containerRef} className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-amber-50 to-orange-50 min-h-screen">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Calculator className="w-8 h-8 text-amber-600" />
          <h1 className="text-4xl font-bold text-gray-800">Calculadora de Ratio</h1>
          <Coffee className="w-8 h-8 text-amber-600" />
        </div>
        <p className="text-gray-600 text-lg">Encuentra la proporción perfecta para tu café</p>
      </div>

      {/* Method Selector - SIN botones de edición */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {Object.entries(brewingMethods).map(([key, method]) => (
          <button
            key={key}
            onClick={() => setSelectedMethod(key)}
            className={`w-full p-4 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
              selectedMethod === key
                ? `${method.color} text-white border-transparent shadow-lg`
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 shadow-sm'
            }`}
          >
            <div className="text-3xl mb-2">{method.icon}</div>
            <div className="font-semibold text-sm">{method.name}</div>
            <div className="text-xs opacity-75 mt-1">
              {getRatioText(method.ratio)}
            </div>
          </button>
        ))}
      </div>

      {/* Edit Ratio Modal */}
      {editingMethod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Editar Ratio - {brewingMethods[editingMethod].name}
              </h3>
              <button
                onClick={cancelEditing}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-2">
                  <Coffee className="w-5 h-5 text-amber-600" />
                  <input
                    type="number"
                    value={tempRatio.coffee}
                    onChange={(e) => handleTempRatioChange('coffee', e.target.value)}
                    className="w-20 p-2 border rounded-lg text-center"
                    min="0.1"
                    step="0.1"
                  />
                </div>
                <span className="text-2xl font-bold text-gray-400">:</span>
                <div className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-blue-600" />
                  <input
                    type="number"
                    value={tempRatio.water}
                    onChange={(e) => handleTempRatioChange('water', e.target.value)}
                    className="w-20 p-2 border rounded-lg text-center"
                    min="0.1"
                    step="0.1"
                  />
                </div>
              </div>
              
              <div className="text-center text-gray-600">
                Ratio resultante: <strong>1:{Math.round(tempRatio.water / tempRatio.coffee * 10) / 10}</strong>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={cancelEditing}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveRatio}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected Method Info - CON botón de edición */}
      <div className={`${currentMethod.color} text-white p-6 rounded-xl mb-8 shadow-lg`}>
        <div className="flex items-center mb-4">
          <span className="text-5xl mr-4">{currentMethod.icon}</span>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-3xl font-bold">{currentMethod.name}</h2>
              {currentMethod.editable && (
                <button
                  onClick={() => startEditingRatio(selectedMethod)}
                  className="bg-white bg-opacity-20 p-2 rounded-lg hover:bg-opacity-30 transition-colors"
                  title="Editar ratio"
                >
                  <Edit3 className="w-5 h-5" />
                </button>
              )}
            </div>
            <p className="text-white/90 text-lg">{currentMethod.description}</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-2xl font-bold">
            Ratio: {getRatioText(currentMethod.ratio)}
          </div>
          <div className="text-right">
            <div className="text-sm opacity-90">Intensidad</div>
            <div className={`text-lg font-semibold ${strength.color}`}>
              {strength.level}
            </div>
          </div>
        </div>
      </div>

      {/* Calculator */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        {/* Coffee Input */}
        <div className="bg-white p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Coffee className="w-6 h-6 text-amber-600" />
              <h3 className="text-xl font-bold text-gray-800">Café</h3>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              calculationMode === 'coffee' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
            }`}>
              {calculationMode === 'coffee' ? 'Base de cálculo' : 'Calculado'}
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cantidad de café (gramos)
              </label>
              <input
                type="number"
                value={coffeeAmount}
                onChange={(e) => handleCoffeeChange(parseFloat(e.target.value) || 0)}
                className="w-full p-3 text-2xl font-bold text-center border-2 rounded-lg focus:border-amber-500 focus:outline-none"
                min="0"
                step="0.1"
              />
            </div>
            
            <div className="text-center">
              <Scale className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-amber-600">{coffeeAmount}g</div>
            </div>
          </div>
        </div>

        {/* Water Input */}
        <div className="bg-white p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Droplets className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-800">Agua</h3>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              calculationMode === 'water' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
            }`}>
              {calculationMode === 'water' ? 'Base de cálculo' : 'Calculado'}
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cantidad de agua (ml/g)
              </label>
              <input
                type="number"
                value={waterAmount}
                onChange={(e) => handleWaterChange(parseFloat(e.target.value) || 0)}
                className="w-full p-3 text-2xl font-bold text-center border-2 rounded-lg focus:border-blue-500 focus:outline-none"
                min="0"
                step="0.1"
              />
            </div>
            
            <div className="text-center">
              <Droplets className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-blue-600">{waterAmount}ml</div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-center gap-4 mb-8">
        <button
          onClick={swapCalculationMode}
          className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
        >
          <ArrowRightLeft className="w-5 h-5" />
          Cambiar Base
        </button>
        <button
          onClick={resetCalculator}
          className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
        >
          <RotateCcw className="w-5 h-5" />
          Reiniciar
        </button>
      </div>

      {/* Results Summary */}
      <div className="bg-white p-6 rounded-xl shadow-lg border">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Resumen del Cálculo</h3>
        
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Ratio Actual</div>
            <div className="text-2xl font-bold text-gray-800">
              1:{Math.round((waterAmount / coffeeAmount) * 10) / 10}
            </div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Absorción</div>
            <div className="text-2xl font-bold text-gray-800">
              {Math.round(getWaterAbsorption() * 10) / 10}ml
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Agua absorbida
            </div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Volumen Final</div>
            <div className="text-2xl font-bold text-gray-800">
              {Math.round(getFinalVolume() * 10) / 10}ml
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Café resultante
            </div>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Tazas</div>
            <div className="text-2xl font-bold text-gray-800">
              {Math.round(getFinalVolume() / 150 * 10) / 10}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (150ml por taza)
            </div>
          </div>
        </div>

        {/* Strength Indicator */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Intensidad del Café</span>
            <span className={`text-sm font-bold ${strength.color}`}>{strength.level}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className={`h-4 rounded-full transition-all duration-500 ${strength.bg}`}
              style={{ width: strength.width }}
            ></div>
          </div>
        </div>

        {/* Recipe Summary */}
        <div className="border-t pt-4">
          <h4 className="font-bold text-gray-800 mb-3">📝 Resumen de la Receta:</h4>
          <div className="text-gray-700 space-y-2 bg-gray-50 p-4 rounded-lg">
            <p>☕ <strong>{coffeeAmount}g</strong> de café molido para <strong>{currentMethod.name}</strong></p>
            <p>💧 <strong>{waterAmount}ml</strong> de agua</p>
            <p>⚖️ Ratio <strong>{getRatioText(currentMethod.ratio)}</strong></p>
            <p>🥤 Resultado: <strong>{Math.round(getFinalVolume() * 10) / 10}ml</strong> de café ({strength.level.toLowerCase()})</p>
            {selectedMethod === 'cold' && (
              <p className="text-blue-600">❄️ <em>Nota: Cold brew es un concentrado, diluye 1:1 con agua o leche al servir</em></p>
            )}
            {selectedMethod === 'espresso' && (
              <p className="text-yellow-600">☕ <em>Nota: Tiempo de extracción ideal 25-30 segundos</em></p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoffeeRatioCalculator;
